import { Address } from '@msdyn365-commerce/retail-proxy';
import { IActionContext } from '@msdyn365-commerce/core';
import getStoreLocationCentiroAction, {
    GetStoreLocationCentiroInput,
    IFullOrgUnitAvailabilityExtended
} from '../../actions/get-store-location-centiro.action';

import { IStoreSelectorLocation } from '@msdyn365-commerce-modules/bopis-utilities';
import { IMapWindow } from '@msdyn365-commerce-modules/bopis-utilities/src/store-selector-state-manager/models/map-data';
import { _readFromSessionStorage, _writeToSessionStorage } from '../../utils/helper';

const CENTIRO_STORES: string = 'bsCentiroStores';

export const loadCentiroStores = async (address: Address = {}, context: IActionContext): Promise<IFullOrgUnitAvailabilityExtended[]> => {
    let network = 'postnord_DK';
    const networkMapping = context.requestContext.app.config.networkMapping;
    if (networkMapping) {
        const networkCode = networkMapping.find((mapping: any) => mapping.countryCode === address?.TwoLetterISORegionName)?.networkCode;
        network = networkCode || 'postnord_DK';
    }
    if (address) {
        const orgUnitStoreInformation = await getStoreLocationCentiroAction(
            new GetStoreLocationCentiroInput(
                address?.TwoLetterISORegionName || '',
                address?.City || '',
                address?.ZipCode || '',
                network,
                address?.Street || '',
                address?.State || '',
                context.requestContext.app.config.collectionPointsRadius.toString() ?? '10',
                context.requestContext.app.config.collectionPointsAmount.toString() ?? '5'
            ),
            context
        );
        if (!orgUnitStoreInformation || orgUnitStoreInformation.length === 0) {
            context.telemetry.error(
                `load-centiro-stores no stores were found | country: ${address?.TwoLetterISORegionName} | network: ${network} | address: ${address.FullAddress} | radius: ${context.requestContext.app.config.collectionPointsRadius} | amount: ${context.requestContext.app.config.collectionPointsAmount}`
            );
            return [];
        }
        setCentiroStores(
            orgUnitStoreInformation.map(item => {
                return {
                    City: item.OrgUnitAvailability?.OrgUnitLocation?.City,
                    Zip: item.OrgUnitAvailability?.OrgUnitLocation?.Zip,
                    Street: item.OrgUnitAvailability?.OrgUnitLocation?.Street,
                    OrgUnitName: item.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName,
                    OrgUnitNumber: item.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber,
                    ExternalId: item.OrgUnitAvailability?.OrgUnitLocation?.ExternalId
                };
            })
        );
        return orgUnitStoreInformation;
    } else {
        return [];
    }
};

export const setCentiroStores = (stores: Object[]) => {
    _writeToSessionStorage(CENTIRO_STORES, JSON.stringify(stores));
};

export const getCentiroStores = (): Object[] | undefined => {
    const data = _readFromSessionStorage(CENTIRO_STORES);
    if (data) {
        return JSON.parse(data) as Object[];
    }
    return;
};

declare const window: IMapWindow;

interface IStoreSelectorLocationExtended extends IStoreSelectorLocation {
    address: {};
}

export const geoLocate = (
    searchTerm: string,
    actionContext: IActionContext
): Promise<IStoreSelectorLocationExtended | undefined> | undefined => {
    if (
        !searchTerm ||
        !actionContext.requestContext.channel ||
        !actionContext.requestContext.channel.BingMapsApiKey ||
        !actionContext.requestContext.channel.BingMapsEnabled
    ) {
        return undefined;
    }
    const SERVICE_ENDPOINT: string = 'https://dev.virtualearth.net/REST/v1/';

    let searchContent: string = searchTerm;

    const jsonp = `mapSearchCallback_${crypto.getRandomValues(new Uint32Array(1))[0]}`;

    if (actionContext.requestContext.channel.ChannelCountryRegionISOCode) {
        searchContent = `${searchTerm.trim()} ${actionContext.requestContext.channel.ChannelCountryRegionISOCode}`;
    }

    const query = encodeURI(searchContent);
    const key = encodeURI(actionContext.requestContext.channel.BingMapsApiKey);

    const geolocationApiUrl =
        actionContext.requestContext.app &&
        actionContext.requestContext.app.config &&
        actionContext.requestContext.app.config.geolocationApiUrl;
    const requestUrl = `${geolocationApiUrl ? geolocationApiUrl : SERVICE_ENDPOINT}/Locations?query=${query}&key=${key}&jsonp=${jsonp}`;

    return new Promise<IStoreSelectorLocationExtended | undefined>(resolve => {
        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('src', requestUrl);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window[jsonp] = (data: any) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete window[jsonp];
            script.remove();

            if (data && data.resourceSets && data.resourceSets.length > 0 && data.resourceSets[0].resources.length > 0) {
                resolve({
                    latitude: data.resourceSets[0].resources[0].point.coordinates[0],
                    longitude: data.resourceSets[0].resources[0].point.coordinates[1],
                    address: data.resourceSets[0].resources[0].address
                });
            }

            resolve(undefined);
        };

        document.body.appendChild(script);
    });
};
